import React, { createContext, useContext, useState } from "react";
import { ProductCategory } from "@utils/constants";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import { DefaultThemeValueType } from "../../../styled";
import { UserResponse } from "@src/types/user";
import { useGetProductById, useSimilarDiamondData } from "@apis/diamondDetail";
import { IncludedCategories } from "@apis/utils/productUtils";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import {
  isDiamondOrLabGrown,
  isLabGrown,
} from "@utils/product/productValidators";
import { useSearchGroupSlice } from "@redux/slices/searchGroupsSlice";
import { useAppState } from "@redux/slices/appStateSlice";
import { body, Diamond } from "@src/types/DiamondDetail";
import { normalizeBoolean } from "@components/atoms/Filters/ButtonSection/Utils";

type FindMatchContextType = {
  navigation: any;
  theme: DefaultThemeValueType;
  t: any;
  typography: any;
  userInfo: UserResponse;
  itemDetail: any;
  setItemDetail: any;
  otherDetail: any[];
  setOtherItemDetail: any;
  currentIndex: number;
  setCurrentIndex: () => any;
  productColor: string;
  productType: string;
  handleItemClick: (item: any) => void;
  loadingData: boolean;
};

const FindMatchContext = createContext<FindMatchContextType | undefined>(
  undefined
);

export const useFindMatch = () => {
  const context = useContext(FindMatchContext);
  if (!context) {
    throw new Error("error");
  }
  return context;
};

export const FindMatchProvider: React.FC<{
  children: React.ReactNode;
  route?: any;
  navigation: any;
  userInfo: any;
  t: any;
  theme: DefaultThemeValueType;
  typography: any;
  productPrefix: string;
}> = ({
  children,
  route,
  navigation,
  userInfo,
  t,
  theme,
  typography,
  productPrefix,
}) => {
  const [itemDetailOverride, setItemDetail] = useState<Diamond>();
  const { selectedGroups } = useSearchGroupSlice();
  const { isCustomApp } = useAppState();
  const productType = route?.params?.productType || "";
  const [otherDetailOverride, setOtherItemDetail] = useState<body>();
  const [currentIndex, setCurrentIndex] = useState(0);
  let enterSecondFlow = route?.params?.enterSecondFlow || false;
  enterSecondFlow = normalizeBoolean(enterSecondFlow);
  const showPairButton = route?.params?.showPairButton || false;

  let getDiamondDetailData = useGetProductById(
    true,
    productType as IncludedCategories,
    route?.params?.itemId as number,
    userInfo,
    userInfo.getUserPriceMode(),
    userInfo.getUserPriceMode() === PriceModes.ORANGE
      ? userInfo.getUserMarkupAmount()
      : 0,
    productType,
    selectedGroups[productType],
    isCustomApp
  );

  let getMatchPairDetailData = useSimilarDiamondData(
    route?.params?.itemId as number,
    userInfo,
    userInfo.getUserPriceMode(),
    userInfo.getUserPriceMode() === PriceModes.ORANGE
      ? userInfo.getUserMarkupAmount()
      : 0,
    false,
    0,
    isLabGrown(productType),
    isDiamondOrLabGrown(productType),
    selectedGroups[productType]
  );

  // Derive data and loading state directly from the queries instead of
  // mirroring them into local state via an effect. The effect-based approach
  // was timing-fragile (especially on iPad, where the state update from the
  // query resolution wasn't flushed reliably), which kept the loader spinning
  // indefinitely until an unrelated re-render forced it to settle.
  const matchQueryEnabled = isDiamondOrLabGrown(productType);

  const derivedItemDetail: Diamond | undefined =
    getDiamondDetailData.data?.data?.response?.body?.diamonds?.[0];
  const derivedOtherDetail: body | undefined =
    getMatchPairDetailData?.data?.data?.diamonds;

  const itemDetail = itemDetailOverride ?? derivedItemDetail;
  const otherDetail = otherDetailOverride ?? derivedOtherDetail;

  const primaryDone =
    getDiamondDetailData.isSuccess || getDiamondDetailData.isError;
  const matchDone =
    !matchQueryEnabled ||
    getMatchPairDetailData.isSuccess ||
    getMatchPairDetailData.isError;

  const loadingData = !(primaryDone && matchDone);

  const getProductColor = (type: string) => {
    switch (type) {
      case ProductCategory.DIAMOND:
        return {
          color: "diam-06",
        };
      case ProductCategory.GEMSTONE:
        return {
          color: "gem-06",
        };
      case ProductCategory.JEWELRY:
        return {
          color: "jewelry-06",
        };
      case ProductCategory.LAB_GROWN_DIAMOND:
        return {
          color: "lab-06",
        };
      case ProductCategory.LABGROWN_JEWELRY:
        return {
          color: "lab-06",
        };
      default:
        return {
          color: "diam-06",
        };
    }
  };
  const { color: productColor } = getProductColor(productType);

  const handleItemClick = (item: any) => {
    if (!item?.id) return;
    const params = {
      productID: item.id,
      productType: item.type || productType,
      productPrefix,
      // The matching stone is a different product. Pass it through so the
      // product page can render immediately instead of waiting on a new
      // request that does not finish on iPad.
      preloadedItem: { ...item, type: item.type || productType },
      navigationRequestId: Date.now(),
      type: undefined,
    };
    navigate(Routes.PDPScreen, params, { merge: true, pop: true });
  };

  const value = {
    navigation,
    userInfo,
    t,
    theme,
    typography,
    itemDetail,
    setItemDetail,
    otherDetail,
    setOtherItemDetail,
    currentIndex,
    setCurrentIndex,
    productColor,
    productType,
    handleItemClick,
    loadingData,
    enterSecondFlow,
    showPairButton,
  };

  return (
    <FindMatchContext.Provider value={value}>
      {children}
    </FindMatchContext.Provider>
  );
};
